import React, { memo, useRef, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { WorkflowItem, WorkflowStatus } from '../../config';
import WorkflowStatusPill from '../../../../components/WorkflowStatusPill';
import IconButton from '../../../../components/IconButton';
import { ChevronLeftIcon } from '../../../../components/IconButton/icons';
import KanbanCard from '../KanbanCard';

// =======================================
// INTERFACES
// =======================================

interface KanbanColumnProps {
  status: WorkflowStatus;
  items: WorkflowItem[];
  collapsed: boolean;
  onToggleCollapsed: (statusId: number) => void;
  onCardClick: (item: WorkflowItem) => void;
  onMoveRequest?: (item: WorkflowItem) => void;
}

// Gap between cards
const GAP = 8;

// =======================================
// VIRTUALIZED CONTENT
// =======================================

interface VirtualizedContentProps {
  items: WorkflowItem[];
  onCardClick: (item: WorkflowItem) => void;
  onMoveRequest?: (item: WorkflowItem) => void;
}

/**
 * The column's scrolling card list. Not keyed on the item list: remounting it
 * whenever a card arrived or left threw the column back to the top after
 * every drop. Rows are keyed on the item, so the virtualizer's measurements
 * follow each card as the list changes.
 */
const VirtualizedContent: React.FC<VirtualizedContentProps> = memo(({ items, onCardClick, onMoveRequest }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getItemKey = useCallback((index: number) => {
    const item = items[index];
    return item ? `${item.entity_type}-${item.id}` : index;
  }, [items]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 112,
    overscan: 5,
    getItemKey,
    gap: GAP,
  });

  return (
    <div ref={scrollContainerRef} className="kanban-column__content">
      {items.length === 0 ? (
        <div className="kanban-column__empty">No jobs</div>
      ) : (
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = items[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <KanbanCard item={item} onClick={onCardClick} onMoveRequest={onMoveRequest} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

VirtualizedContent.displayName = 'VirtualizedContent';

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * One status lane. The whole lane is the drop target, collapsed or not, so a
 * card can be dropped on Complete without opening it. The header's pill uses
 * the same colour as the project modal's status pill.
 */
const KanbanColumn: React.FC<KanbanColumnProps> = memo(({
  status,
  items,
  collapsed,
  onToggleCollapsed,
  onCardClick,
  onMoveRequest,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `column-${status.id}`,
    data: { statusId: status.id },
  });

  const toggle = useCallback(() => onToggleCollapsed(status.id), [onToggleCollapsed, status.id]);

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        data-status-id={status.id}
        className={`kanban-column is-collapsed ${isOver ? 'is-over' : ''}`.trim()}
        role="button"
        tabIndex={0}
        aria-label={`Show ${status.status_name} (${items.length})`}
        title={`Show ${status.status_name}`}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="seg-count">{items.length}</span>
        <WorkflowStatusPill status={status.status_name} className="is-vertical" />
      </div>
    );
  }

  return (
    <section
      ref={setNodeRef}
      data-status-id={status.id}
      className={`kanban-column ${isOver ? 'is-over' : ''}`.trim()}
      aria-label={status.status_name}
    >
      <header className="kanban-column__header">
        <WorkflowStatusPill status={status.status_name} />
        <span className="seg-count">{items.length}</span>
        <IconButton
          size="sm"
          className="kanban-column__collapse"
          icon={<ChevronLeftIcon />}
          label={`Collapse ${status.status_name}`}
          onClick={toggle}
        />
      </header>

      <VirtualizedContent
        items={items}
        onCardClick={onCardClick}
        onMoveRequest={onMoveRequest}
      />
    </section>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
